import dayjs from "dayjs";
import { countries } from "countries-list";

// Ticket custom field values are stored as strings. Multiselect answers keep
// their list shape as JSON, everything else is the plain value.
const parseList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [value];
  } catch {
    return value ? value.split(",").map((item) => item.trim()) : [];
  }
};

const isEmpty = (value) =>
  value === null || value === undefined || value === "";

export const BOOLEAN_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

// Options come either as the event's stored definitions ({ name, nameI18n }),
// as one of the fixed { value, label } lists, or as plain strings.
export const optionData = (options = []) =>
  options.map((option) =>
    typeof option === "string" || option.value !== undefined
      ? option
      : option.name
  );

const optionValue = (option) =>
  typeof option === "string" ? option : (option.value ?? option.name);

// An answer submitted on the registration form holds the option's name in the
// attendee's language, so it is matched against every translation of it.
const canonicalOption = (value, options = []) => {
  const target = String(value).toLowerCase();

  const match = options.find((option) => {
    if (typeof option === "string") return option.toLowerCase() === target;

    return [
      option.name,
      option.value,
      option.label,
      ...Object.values(option.nameI18n || {}),
    ].some((name) => name?.toLowerCase?.() === target);
  });

  return match ? optionValue(match) : String(value);
};

export const formatCustomFieldValue = (value, field) => {
  if (isEmpty(value)) return "";

  switch (field?.type) {
    case "date": {
      const date = dayjs(value);
      return date.isValid() ? date.format("DD.MM.YYYY.") : String(value);
    }
    case "boolean":
      if (value === "true" || value === true) return "Yes";
      return value === "false" || value === false ? "No" : String(value);
    case "country":
      return countries[value]?.name || String(value);
    case "select":
      return canonicalOption(value, field.options);
    case "multiselect":
      return parseList(value)
        .map((item) => canonicalOption(item, field.options))
        .join(", ");
    default:
      return String(value);
  }
};

// String out of the database into the shape the typed input expects.
export const toCustomFieldInputValue = (value, type, options) => {
  if (isEmpty(value)) {
    return type === "multiselect" ? [] : type === "boolean" ? false : null;
  }

  switch (type) {
    case "date": {
      const date = dayjs(value);
      return date.isValid() ? date.format("YYYY-MM-DD") : null;
    }
    case "boolean":
      return value === "true" || value === true;
    case "multiselect":
      return parseList(value).map((item) => canonicalOption(item, options));
    case "select":
      return canonicalOption(value, options);
    default:
      return String(value);
  }
};

// Typed input value back into the string the API stores.
export const fromCustomFieldInputValue = (value, type) => {
  if (value === null || value === undefined) return "";

  switch (type) {
    case "date": {
      const date = dayjs(value);
      return date.isValid() ? date.format("YYYY-MM-DD") : "";
    }
    case "boolean":
      return value === "true" || value === true ? "true" : "false";
    case "multiselect":
      return value.length ? JSON.stringify(value) : "";
    default:
      return String(value);
  }
};
